import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { formatCpf } from "../../dp/utils/format";
import {
  ImportUsersXlsxResult,
  importUsersXlsxApi,
} from "../api/adminUsersApi";

import "../pages/AdminSistema.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Chamado quando a importação cria ao menos um usuário. */
  onImported?: () => void;
};

const COLUNAS_ESPERADAS =
  "NOME, TURNO, FUNÇÃO, CELULAR, ADMISSÃO, DT NASCIMENTO, CPF, EMPRESA, PROJETO, UNIDADE";

const ImportUsersXlsxModal: React.FC<Props> = ({ isOpen, onClose, onImported }) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [file, setFile] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<ImportUsersXlsxResult | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setResultado(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !enviando) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = anterior;
    };
  }, [isOpen, onClose, enviando]);

  const handleImport = async () => {
    if (!token || !file) return;

    try {
      setEnviando(true);
      const r = await importUsersXlsxApi(token, file);
      setResultado(r);

      if (r.createdCount > 0) {
        toast.success(
          `${r.createdCount} usuário(s) criado(s) de ${r.totalRows} linha(s).`
        );
        onImported?.();
      } else {
        toast.info("Nenhum usuário foi criado. Veja o relatório.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao importar a planilha.");
    } finally {
      setEnviando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="admModalOverlay"
      onClick={() => {
        if (!enviando) onClose();
      }}
    >
      <div
        className="admModal"
        style={{ maxWidth: 680 }}
        role="dialog"
        aria-modal="true"
        aria-label="Importar usuários por planilha"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admModal__header">
          <h2 className="admModal__title">Importar usuários por planilha</h2>
          <button
            type="button"
            className="admModal__close"
            onClick={onClose}
            disabled={enviando}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div className="admModal__body">
          {resultado ? (
            <>
              <div className="admImportTotals">
                <div className="admImportTotal">
                  <div className="admImportTotal__value">{resultado.totalRows}</div>
                  <div className="admImportTotal__label">Linhas lidas</div>
                </div>
                <div className="admImportTotal admImportTotal--created">
                  <div className="admImportTotal__value">{resultado.createdCount}</div>
                  <div className="admImportTotal__label">Criados</div>
                </div>
                <div className="admImportTotal">
                  <div className="admImportTotal__value">{resultado.skippedCount}</div>
                  <div className="admImportTotal__label">Pulados</div>
                </div>
                <div className="admImportTotal admImportTotal--problem">
                  <div className="admImportTotal__value">{resultado.problemCount}</div>
                  <div className="admImportTotal__label">Com problema</div>
                </div>
              </div>

              {resultado.createdCount > 0 ? (
                <div className="admNotice" style={{ marginBottom: 0 }}>
                  Os usuários criados nascem com a senha <strong>Senha@123</strong> e são
                  obrigados a trocá-la no primeiro acesso.
                </div>
              ) : null}

              {resultado.problems.length > 0 ? (
                <div className="admFormGroup">
                  <label>Linhas com problema ({resultado.problems.length})</label>
                  <div className="admImportRows">
                    <table className="admTable">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Linha</th>
                          <th>Nome / CPF</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultado.problems.map((p, i) => (
                          <tr key={`${p.row}-${i}`}>
                            <td>{p.row}</td>
                            <td>
                              <div>{p.nomeCompleto || "—"}</div>
                              <div className="admMuted">
                                {p.cpf ? formatCpf(p.cpf) : "—"}
                                {p.unidade ? ` · ${p.unidade}` : ""}
                              </div>
                            </td>
                            <td>{p.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <span className="admFormHint">
                    Corrija estas linhas na planilha e reenvie — as já criadas serão puladas.
                  </span>
                </div>
              ) : null}

              {resultado.skipped.length > 0 ? (
                <div className="admFormGroup">
                  <label>Linhas puladas ({resultado.skipped.length})</label>
                  <div className="admImportRows">
                    <table className="admTable">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Linha</th>
                          <th>Nome / CPF</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultado.skipped.map((s, i) => (
                          <tr key={`${s.row}-${i}`}>
                            <td>{s.row}</td>
                            <td>
                              <div>{s.nomeCompleto || "—"}</div>
                              <div className="admMuted">
                                {s.cpf ? formatCpf(s.cpf) : "—"}
                              </div>
                            </td>
                            <td>{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {resultado.problems.length === 0 && resultado.skipped.length === 0 ? (
                <p style={{ margin: 0, fontSize: 14 }}>
                  Todas as linhas foram importadas sem problemas.
                </p>
              ) : null}
            </>
          ) : (
            <>
              <div className="admNotice" style={{ marginBottom: 0 }}>
                <strong>Formato esperado:</strong> o cabeçalho fica na <strong>linha 2</strong> e
                os dados começam na <strong>linha 3</strong>. Colunas: {COLUNAS_ESPERADAS}.
              </div>

              <p className="admMuted" style={{ margin: 0 }}>
                A função define o tipo do usuário e a unidade define a loja vinculada. Linhas com
                CPF já cadastrado são puladas; linhas com função ou unidade desconhecida entram no
                relatório de problemas — nada é criado pela metade.
              </p>

              <p className="admMuted" style={{ margin: 0 }}>
                Os usuários criados nascem com a senha <strong>Senha@123</strong> e precisam
                trocá-la no primeiro acesso para entrar.
              </p>

              <div className="admFormGroup">
                <label htmlFor="admImportFile">Arquivo .xlsx</label>
                <input
                  id="admImportFile"
                  className="admFileInput"
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  disabled={enviando}
                />
              </div>
            </>
          )}
        </div>

        <div className="admModal__footer">
          {resultado ? (
            <button type="button" className="admBtn admBtn--primary" onClick={onClose}>
              Fechar
            </button>
          ) : (
            <>
              <button
                type="button"
                className="admBtn admBtn--secondary"
                onClick={onClose}
                disabled={enviando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admBtn admBtn--primary"
                onClick={handleImport}
                disabled={!file || enviando}
              >
                {enviando ? "Importando..." : "Importar"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportUsersXlsxModal;
